'use client';

import React, { useState } from 'react';

import EducationCard from './EducationCard';
import CertificationCard from './CertificationCard';
import SkillCategory from './SkillCategory';


interface Skill {
  name: string;
  proficiency: number;
  description: string;
  yearsExperience: string;
}

interface SkillCategoryData {
  title: string;
  icon: string;
  description: string;
  skills: Skill[];
  color: string;
}

interface Education {
  degree: string;
  institution: string;
  location: string;
  year: string;
  description: string;
  icon: string;
}

interface Certification {
  title: string;
  issuer: string;
  issueDate: string;
  expiryDate?: string;
  credentialId: string;
  verificationUrl: string;
  status: 'active' | 'expired' | 'pending';
  image?: string;
}

interface SkillData {
  category: string;
  proficiency: number;
  fullMark: number;
}

const SkillsInteractive: React.FC = () => {
  const [activeCertTab, setActiveCertTab] = useState('education');

  const educationData: Education[] = [
  {
    degree: 'Bachelor of Science in Biology',
    institution: 'Grambling State University',
    location: 'Grambling, LA',
    year: '1997',
    description:
    'Comprehensive study in Biology with a minor focused on analytical chemistry, organic chemistry, and laboratory techniques. Graduated with honors, focusing on environmental chemistry and quality control methodologies.',
    icon: 'AcademicCapIcon'
  }];

  const certificationData: Certification[] = [
  {
    title: 'Certified Laboratory Manager (CLM)',
    issuer: 'American Association for Laboratory Accreditation',
    issueDate: 'March 2015',
    expiryDate: 'March 2027',
    credentialId: 'CLM-2015-08742',
    verificationUrl: 'https://www.a2la.org/verify',
    status: 'active',
    image: 'https://images.unsplash.com/photo-1554224311-beee4ece8c35?w=800&h=600&fit=crop'
  },
  {
    title: 'ISO/IEC 17025 Lead Auditor',
    issuer: 'International Register of Certificated Auditors',
    issueDate: 'June 2016',
    expiryDate: 'June 2028',
    credentialId: 'IRCA-LA-17025-9834',
    verificationUrl: 'https://www.irca.org/verify',
    status: 'active',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=600&fit=crop'
  },
  {
    title: 'Good Laboratory Practice (GLP) Certification',
    issuer: 'Society of Quality Assurance',
    issueDate: 'January 2014',
    expiryDate: 'January 2027',
    credentialId: 'SQA-GLP-2014-5621',
    verificationUrl: 'https://www.sqa.org/verify',
    status: 'active',
    image: 'https://images.unsplash.com/photo-1532094349884-543bc11b234d?w=800&h=600&fit=crop'
  },
  {
    title: 'OSHA 30-Hour General Industry Safety',
    issuer: 'Occupational Safety and Health Administration',
    issueDate: 'September 2018',
    credentialId: 'OSHA-30-2018-12456',
    verificationUrl: 'https://www.osha.gov/verify',
    status: 'active',
    image: 'https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=800&h=600&fit=crop'
  },
  {
    title: 'Six Sigma Black Belt',
    issuer: 'American Society for Quality',
    issueDate: 'May 2017',
    credentialId: 'ASQ-SSBB-2017-7893',
    verificationUrl: 'https://www.asq.org/verify',
    status: 'active',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop'
  },
  {
    title: 'Project Management Professional (PMP)',
    issuer: 'Project Management Institute',
    issueDate: 'November 2019',
    expiryDate: 'November 2025',
    credentialId: 'PMI-PMP-2019-45678',
    verificationUrl: 'https://www.pmi.org/verify',
    status: 'pending',
    image: 'https://images.unsplash.com/photo-1507925921958-8a62f3d1a50d?w=800&h=600&fit=crop'
  }];

  const skillCategories: SkillCategoryData[] = [
  {
    title: "Laboratory Operations Management",
    icon: "BeakerIcon",
    description: "Comprehensive expertise in managing complex laboratory operations, ensuring regulatory compliance, and optimizing workflows for maximum efficiency.",
    color: "blue",
    skills: [
    {
      name: "LIMS Implementation & Management",
      proficiency: 95,
      description: "Expert in Laboratory Information Management Systems deployment, configuration, and optimization for streamlined data management and regulatory compliance.",
      yearsExperience: "10+ years"
    },
    {
      name: "QA/QC Program Development",
      proficiency: 98,
      description: "Proven track record in designing and implementing comprehensive Quality Assurance and Quality Control programs that exceed industry standards.",
      yearsExperience: "12+ years"
    },
    {
      name: "Regulatory Compliance (EPA, FDA, ISO)",
      proficiency: 96,
      description: "Deep understanding of EPA, FDA, and ISO regulatory requirements with consistent success in audits and inspections.",
      yearsExperience: "15+ years"
    },
    {
      name: "Laboratory Accreditation Management",
      proficiency: 92,
      description: "Experienced in maintaining laboratory accreditations including ISO 17025, NELAC, and industry-specific certifications.",
      yearsExperience: "8+ years"
    }]

  },
  {
    title: "Leadership & Team Development",
    icon: "UserGroupIcon",
    description: "Strategic leadership capabilities focused on building high-performing teams, fostering professional growth, and creating cultures of excellence.",
    color: "emerald",
    skills: [
    {
      name: "Team Building & Staff Development",
      proficiency: 94,
      description: "Skilled in recruiting, training, and developing laboratory professionals while creating collaborative and productive work environments.",
      yearsExperience: "12+ years"
    },
    {
      name: "Performance Management",
      proficiency: 90,
      description: "Effective in setting clear expectations, providing constructive feedback, and implementing performance improvement strategies.",
      yearsExperience: "10+ years"
    },
    {
      name: "Change Management",
      proficiency: 88,
      description: "Adept at leading organizational transformations, implementing new technologies, and managing resistance to change.",
      yearsExperience: "8+ years"
    },
    {
      name: "Cross-Functional Collaboration",
      proficiency: 92,
      description: "Strong ability to work across departments, align stakeholders, and drive collaborative problem-solving initiatives.",
      yearsExperience: "15+ years"
    }]

  },
  {
    title: "Business Operations & Strategy",
    icon: "ChartBarIcon",
    description: "Business acumen combining operational excellence with strategic planning to drive profitability and sustainable growth.",
    color: "purple",
    skills: [
    {
      name: "P&L Management",
      proficiency: 85,
      description: "Experienced in managing laboratory budgets, controlling costs, and optimizing resource allocation for profitability.",
      yearsExperience: "7+ years"
    },
    {
      name: "Process Optimization",
      proficiency: 93,
      description: "Expert in identifying inefficiencies, implementing lean methodologies, and driving continuous improvement initiatives.",
      yearsExperience: "12+ years"
    },
    {
      name: "Client Relationship Management",
      proficiency: 89,
      description: "Strong track record in building lasting client relationships, understanding needs, and delivering exceptional service.",
      yearsExperience: "10+ years"
    },
    {
      name: "Strategic Planning & Execution",
      proficiency: 87,
      description: "Capable of developing long-term strategies, setting measurable goals, and executing plans that drive business results.",
      yearsExperience: "8+ years"
    }]

  },
  {
    title: "Technical Laboratory Skills",
    icon: "WrenchScrewdriverIcon",
    description: "Hands-on technical expertise across multiple analytical methods and instrumentation platforms.",
    color: "amber",
    skills: [
    {
      name: "Analytical Chemistry Methods",
      proficiency: 91,
      description: "Proficient in GC, HPLC, ICP-MS, and other advanced analytical techniques for environmental and pharmaceutical testing.",
      yearsExperience: "15+ years"
    },
    {
      name: "Method Development & Validation",
      proficiency: 88,
      description: "Experienced in developing, validating, and optimizing analytical methods to meet regulatory requirements.",
      yearsExperience: "10+ years"
    },
    {
      name: "Data Analysis & Interpretation",
      proficiency: 90,
      description: "Strong analytical skills in reviewing complex data sets, identifying trends, and making data-driven decisions.",
      yearsExperience: "15+ years"
    },
    {
      name: "Instrumentation Troubleshooting",
      proficiency: 86,
      description: "Skilled in diagnosing and resolving technical issues with laboratory instrumentation to minimize downtime.",
      yearsExperience: "12+ years"
    }]

  },
  {
    title: "Telecommunications & Technical Services",
    icon: "SignalIcon",
    description: "Technical expertise in telecommunications infrastructure, network maintenance, and customer service delivery.",
    color: "indigo",
    skills: [
    {
      name: "HFC Plant Maintenance",
      proficiency: 88,
      description: "Experienced in maintaining hybrid fiber-coaxial plant infrastructure ensuring optimal network performance and reliability.",
      yearsExperience: "10+ years"
    },
    {
      name: "Networking & Systems Analysis",
      proficiency: 85,
      description: "Skilled in analyzing network systems, diagnosing issues, and implementing solutions for telecommunications services.",
      yearsExperience: "10+ years"
    },
    {
      name: "Technical Troubleshooting",
      proficiency: 90,
      description: "Expert in diagnosing and resolving complex technical issues in telecommunications and network infrastructure.",
      yearsExperience: "10+ years"
    },
    {
      name: "Customer Service Excellence",
      proficiency: 92,
      description: "Proven ability to deliver exceptional customer service while resolving technical issues efficiently.",
      yearsExperience: "10+ years"
    }]

  },
  {
    title: "Logistics & Operations Management",
    icon: "TruckIcon",
    description: "Comprehensive experience in logistics operations, warehouse management, and regulatory compliance in freight forwarding.",
    color: "teal",
    skills: [
    {
      name: "Multi-Site Operations Management",
      proficiency: 87,
      description: "Skilled in managing operations across multiple locations, coordinating resources, and ensuring consistent service delivery.",
      yearsExperience: "3+ years"
    },
    {
      name: "Inventory Management",
      proficiency: 90,
      description: "Expert in inventory control, warehouse organization, and supply chain coordination for optimal efficiency.",
      yearsExperience: "15+ years"
    },
    {
      name: "TSA/FAA Compliance",
      proficiency: 93,
      description: "Deep knowledge of aviation security regulations and proven track record in maintaining perfect compliance records.",
      yearsExperience: "15+ years"
    },
    {
      name: "Contract Negotiation",
      proficiency: 84,
      description: "Experienced in negotiating contracts with vendors and service providers to optimize costs and service quality.",
      yearsExperience: "3+ years"
    },
    {
      name: "Business Process Improvement",
      proficiency: 88,
      description: "Proven ability to identify inefficiencies and implement process improvements driving operational excellence.",
      yearsExperience: "15+ years"
    }]

  }];

  const radarData: SkillData[] = [
  { category: "Lab Operations", proficiency: 95, fullMark: 100 },
  { category: "Leadership", proficiency: 92, fullMark: 100 },
  { category: "Compliance", proficiency: 96, fullMark: 100 },
  { category: "Business Mgmt", proficiency: 87, fullMark: 100 },
  { category: "Technical Skills", proficiency: 90, fullMark: 100 },
  { category: "Quality Systems", proficiency: 94, fullMark: 100 }];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary via-primary/95 to-secondary py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="font-heading font-bold text-4xl lg:text-5xl text-white mb-4">
              Education & Credentials
            </h1>
            <p className="text-lg text-white/90 max-w-4xl mx-auto">
              Comprehensive technical and leadership capabilities developed through 15+ years of laboratory operations management, regulatory compliance, and team development across environmental and oil and gas industries.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16 space-y-12">
        {/* Credentials & Education Section */}
        <section>
          {/* Tab Navigation */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex rounded-lg border border-border bg-muted p-1">
              <button
                onClick={() => setActiveCertTab('education')}
                className={`px-6 py-2 rounded-md text-sm font-semibold transition-all duration-200 ${
                  activeCertTab === 'education' ?'bg-primary text-primary-foreground shadow-subtle' :'text-text-secondary hover:text-text-primary'
                }`}
              >
                Education
              </button>
              <button
                onClick={() => setActiveCertTab('certifications')}
                className={`px-6 py-2 rounded-md text-sm font-semibold transition-all duration-200 ${
                  activeCertTab === 'certifications'
                    ? 'bg-primary text-primary-foreground shadow-subtle' :'text-text-secondary hover:text-text-primary'
                }`}
              >
                Certifications
              </button>
            </div>
          </div>

          {/* Tab Content */}
          {activeCertTab === 'education' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {educationData.map((edu, index) => (
                <EducationCard key={index} {...edu} />
              ))}
            </div>
          )}

          {activeCertTab === 'certifications' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {certificationData.map((cert, index) => (
                <CertificationCard key={index} {...cert} />
              ))}
            </div>
          )}
        </section>

        {/* Core Competencies Section */}
        <section>
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl lg:text-4xl text-text-primary mb-4">
              Core Competencies
            </h2>
            <p className="text-text-secondary text-lg max-w-3xl mx-auto">
              Comprehensive skill set spanning technical expertise, leadership capabilities, and business acumen
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {skillCategories.map((category, index) =>
            <SkillCategory
              key={index}
              title={category.title}
              icon={category.icon}
              description={category.description}
              skills={category.skills}
              color={category.color} />

            )}
          </div>
        </section>
      </div>
    </div>);

};

export default SkillsInteractive;